import { useState, useEffect } from 'react';
import Header from '@/components/feature/Header';
import FooterSection from '@/pages/home/components/FooterSection';
import SchemaOrg from '@/components/base/SchemaOrg';
import { buildArticleSchema, buildFAQPageSchema } from '@/mocks/schema-data';

export default function ArticlePage() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleGoToMain = () => {
    window.open('https://kaspreadsheet.com', '_blank', 'noopener,nofollow');
  };

  const guideSchema = buildArticleSchema('sugargoo-spreadsheet-guide');
  const guideFaqSchema = buildFAQPageSchema('sugargoo-spreadsheet-guide');

  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg data={guideSchema} />
      {guideFaqSchema && <SchemaOrg data={guideFaqSchema} />}
      <Header />

      {/* Article Hero */}
      <section className="relative w-full min-h-[400px] md:min-h-[480px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20workspace%20with%20an%20open%20notebook%20a%20laptop%20displaying%20a%20clean%20spreadsheet%20and%20a%20cup%20of%20coffee%20soft%20morning%20sunlight%20streaming%20through%20sheer%20curtains%20minimalist%20decor%20with%20a%20small%20potted%20plant%20cream%20and%20sage%20color%20palette%20calm%20productive%20atmosphere%20editorial%20photography&width=1600&height=800&seq=sugargoo-article-hero&orientation=landscape"
            alt="Sugargoo Spreadsheet Guide hero background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"></div>
        </div>

        <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center py-16">
          <span className="inline-block text-xs font-medium text-primary-300 bg-background-50/20 backdrop-blur-sm px-3 py-1 rounded-full mb-4 whitespace-nowrap">
            Beginner Guide
          </span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl text-background-50 mb-4 leading-tight">
            The Ultimate Sugargoo Spreadsheet Guide for Beginners
          </h1>
          <p className="text-background-50/80 text-sm md:text-base max-w-lg mx-auto">
            Everything you need to know about finding, using and maximizing the best Sugargoo Spreadsheet
            resources for smarter shopping in 2026.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
            <a href="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">Home</a>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-[10px]"></i>
            </span>
            <span className="text-foreground-400 whitespace-nowrap">Sugargoo Spreadsheet Guide</span>
          </nav>

          {/* Intro */}
          <div className="prose-custom mb-10">
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              If you have ever tried shopping from Chinese marketplaces like Taobao, Weidian, or 1688, you probably know
              how overwhelming the experience can be. With millions of product listings, inconsistent quality, and a language
              barrier to navigate, finding exactly what you want often feels like searching for a needle in a haystack.
              This is exactly where a <strong>Sugargoo Spreadsheet</strong> changes everything.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              A <strong>Sugargoo Spreadsheet</strong> is essentially a curated product discovery database that organizes
              thousands of verified finds into easy-to-browse categories. Instead of manually digging through endless listings,
              you get access to a structured collection of trending products complete with direct links, QC photos, size
              references, price comparisons, and community reviews. It is the single most powerful tool for anyone serious
              about building a quality haul through a Chinese shopping agent like Sugargoo.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              In this comprehensive guide, we will walk you through everything you need to know about finding, understanding,
              and making the most of the best <strong>Sugargoo Spreadsheet</strong> resources available today. Whether you
              are a complete beginner placing your first order or an experienced shopper looking to level up your research
              game, this guide has you covered.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-background-100/60 border border-background-200/70 rounded-lg p-5 md:p-6 mb-10">
            <h3 className="font-heading text-lg text-foreground-950 mb-4">Table of Contents</h3>
            <ul className="space-y-2">
              {[
                'What Exactly Is a Sugargoo Spreadsheet?',
                'Why You Need a Sugargoo Spreadsheet for Shopping',
                'How to Find the Best Sugargoo Spreadsheet',
                'Key Features of a Quality Sugargoo Spreadsheet',
                'How to Use a Sugargoo Spreadsheet Effectively',
                'Popular Product Categories to Explore',
                'Sugargoo Spreadsheet vs Traditional Searching',
                'Tips for Beginners Starting with Sugargoo',
                'Common Mistakes to Avoid',
                'Frequently Asked Questions',
                'Final Thoughts and Next Steps',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-primary-500 text-xs font-medium mt-0.5 whitespace-nowrap">{String(i + 1).padStart(2, '0')}.</span>
                  <span className="text-sm text-foreground-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              What Exactly Is a Sugargoo Spreadsheet?
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              At its core, a <strong>Sugargoo Spreadsheet</strong> is a community-driven or curator-maintained database
              that catalogs products available through Sugargoo — one of the most popular Chinese shopping agents on the market.
              Think of it as a massive, organized wishlist that someone else has already done the hard work of putting together
              for you. Each spreadsheet typically contains hundreds or even thousands of individual product entries,
              organized by category, style, price range, or popularity.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              The beauty of a well-maintained <strong>Sugargoo Spreadsheet</strong> lies in its structure. Rather than being
              a random list of links, it usually includes detailed columns for product names, direct shopping links,
              price information (often in both CNY and your local currency), available sizes, color options, QC (Quality Check)
              photo references, seller ratings, and sometimes even fit recommendations from people who have already purchased
              the item. This level of organization turns what could be hours of chaotic browsing into a streamlined,
              enjoyable discovery process.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Many spreadsheets also include special sections for trending items, seasonal collections, budget-friendly
              hidden gems, and newly discovered stores. Some curators even add personal notes about fabric quality,
              shipping weight estimates, and whether an item runs true to size — details that are invaluable when you
              cannot physically see or try on the product before buying. <a href="/" className="text-primary-600 hover:text-primary-700 underline">Return to our homepage</a> to explore all the
              categories we have organized for you.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              Why You Need a Sugargoo Spreadsheet for Shopping
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Shopping through Chinese marketplaces without a <strong>Sugargoo Spreadsheet</strong> is like walking into
              the world&apos;s largest department store blindfolded. You might eventually find something good, but the process
              will be slow, frustrating, and you will almost certainly miss out on better options along the way. Here is
              why incorporating a spreadsheet into your shopping workflow is a game-changer:
            </p>
            <ul className="space-y-3 mb-4">
              {[
                { title: 'Massive Time Savings', desc: 'Instead of spending 4-5 hours manually searching for a single category of products, a well-organized Sugargoo Spreadsheet can cut your research time down to 20-30 minutes. All the filtering and vetting has already been done for you.' },
                { title: 'Quality Assurance', desc: 'Products listed in reputable spreadsheets have usually been vetted by the community. You benefit from collective experience — if an item had poor quality or an unreliable seller, it typically does not make it into a curated spreadsheet.' },
                { title: 'Price Transparency', desc: 'Spreadsheets often include price ranges, helping you quickly identify fair market value and avoid overpriced listings. Some even track price history so you know if an item is currently at a good price point.' },
                { title: 'Discovery of Hidden Gems', desc: 'The best Sugargoo Spreadsheet resources include lesser-known stores and unique products you would probably never stumble upon through normal browsing. This is where you find the truly special items that set your haul apart.' },
                { title: 'Community Insights', desc: 'Many spreadsheets incorporate feedback from real buyers — comments about sizing accuracy, material feel, washing durability, and whether the product matched the listing photos. This is information you simply cannot get from a product page alone.' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0 rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  <div>
                    <strong className="text-sm text-foreground-800">{item.title}: </strong>
                    <span className="text-sm text-foreground-600">{item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              How to Find the Best Sugargoo Spreadsheet
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Not all spreadsheets are created equal. Some are meticulously maintained and updated weekly, while others
              may contain outdated links, deleted listings, or products from unreliable sellers. Here is how to identify
              and locate the highest-quality <strong>Sugargoo Spreadsheet</strong> resources:
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              First, look for spreadsheets that are actively maintained. Check when the last update was made — if the most
              recent revision is more than three months old, many of the product links may no longer work. The best
              <strong>Sugargoo Spreadsheet</strong> curators typically update their databases at least once per week,
              removing dead links and adding fresh discoveries.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Second, prioritize spreadsheets that include detailed product information beyond just a link and a price.
              Look for columns covering QC photo availability, seller ratings, size charts, weight estimates (important
              for calculating shipping costs), and community review notes. The more data points included, the more
              confident you can be in your purchasing decisions.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Our platform at <strong>Sugargoo Sheet</strong> organizes the best <strong>Sugargoo Spreadsheet</strong> categories
              into an easy-to-navigate interface. <a href="/" className="text-primary-600 hover:text-primary-700 underline">Browse our homepage</a> to explore
              all available product categories — from shoes and hoodies to accessories and bags — each linking to
              comprehensive product collections on our main website.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              Key Features of a Quality Sugargoo Spreadsheet
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {[
                { icon: 'ri-links-line', title: 'Direct Product Links', desc: 'Every entry should include a working link to the original product listing on Taobao, Weidian, or 1688.' },
                { icon: 'ri-image-line', title: 'QC Photo References', desc: 'Quality check photos from previous buyers help you verify that the product matches the listing.' },
                { icon: 'ri-money-dollar-circle-line', title: 'Price Information', desc: 'Clear pricing in both CNY and your local currency, sometimes with historical price tracking.' },
                { icon: 'ri-ruler-line', title: 'Size & Fit Data', desc: 'Size charts, fit recommendations, and notes about whether items run large or small.' },
                { icon: 'ri-star-line', title: 'Seller Ratings', desc: 'Information about seller reliability, shipping speed, and overall reputation.' },
                { icon: 'ri-refresh-line', title: 'Regular Updates', desc: 'Active maintenance with weekly or bi-weekly updates to remove dead links and add new finds.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg">
                  <span className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-100/70 text-primary-600">
                    <i className={`${item.icon} text-sm`}></i>
                  </span>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-800 mb-1">{item.title}</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              How to Use a Sugargoo Spreadsheet Effectively
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Having access to a great <strong>Sugargoo Spreadsheet</strong> is only half the battle — knowing how to use
              it efficiently is what separates casual browsers from smart shoppers. Here is a step-by-step approach to
              getting the most value out of your spreadsheet:
            </p>
            <div className="space-y-4 mb-4">
              {[
                { step: '1', title: 'Start with a Clear Goal', desc: 'Before diving in, decide what you are looking for. Are you building a seasonal wardrobe refresh? Searching for a specific sneaker release? Hunting for accessories? Having a clear objective prevents you from getting lost in endless scrolling and impulse-adding items you do not actually need.' },
                { step: '2', title: 'Filter by Category First', desc: 'A good Sugargoo Spreadsheet is organized by category for a reason. Jump straight to the section that matches your goal — whether that is Shoes, Hoodies, Accessories, or Bags — rather than scrolling through the entire database. Our platform makes this especially easy with our category navigation system. <a href="/" className="text-primary-600 hover:text-primary-700 underline">Check out our categorized navigation here.</a>' },
                { step: '3', title: 'Check Multiple Entries Before Deciding', desc: 'Do not settle for the first interesting product you see. Browse at least 5-10 entries in your target category to get a sense of the price range, quality level, and style variety available. This comparative approach almost always leads to better purchases.' },
                { step: '4', title: 'Read Community Notes Carefully', desc: 'Pay close attention to any notes about sizing, material quality, or seller reliability. These real-world insights from previous buyers can save you from costly mistakes — especially with clothing items where fit is everything.' },
                { step: '5', title: 'Build Your Cart Strategically', desc: 'Once you have identified your favorite items, copy their links and add them to your Sugargoo cart. Consider combining items from the same seller to save on domestic shipping within China, and keep an eye on the total estimated weight to manage your international shipping costs.' },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-4 p-4 bg-background-100/60 rounded-lg">
                  <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-500 text-background-50 text-sm font-medium">
                    {item.step}
                  </span>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-800 mb-1">{item.title}</h4>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.desc }}></p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              Popular Product Categories to Explore
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              A comprehensive <strong>Sugargoo Spreadsheet</strong> typically covers a wide range of product categories.
              Here are the most popular ones that shoppers consistently find the best value in:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
              {[
                { name: 'Shoes', desc: 'Sneakers, boots, slides, and everything in between — often at 40-60% less than retail.' },
                { name: 'Hoodies / Sweaters', desc: 'Cozy streetwear essentials with impressive quality for the price point.' },
                { name: 'T-Shirts', desc: 'Graphic tees, basics, and designer-inspired pieces at unbeatable prices.' },
                { name: 'Jackets', desc: 'From lightweight windbreakers to heavy winter coats — seasonal staples covered.' },
                { name: 'Pants / Shorts', desc: 'Cargo pants, denim, track pants and summer shorts in all styles.' },
                { name: 'Accessories', desc: 'Watches, sunglasses, jewelry and small leather goods that elevate any outfit.' },
                { name: 'Bags', desc: 'Backpacks, crossbody bags, totes and luggage for every occasion.' },
                { name: 'Jersey', desc: 'Sports jerseys and athletic wear from basketball to football styles.' },
                { name: 'Headwear', desc: 'Caps, beanies, bucket hats and more to complete your look.' },
              ].map((cat) => (
                <div key={cat.name} className="p-3 bg-background-50 border border-background-200/70 rounded-lg">
                  <h4 className="text-sm font-medium text-foreground-800 mb-1 whitespace-nowrap">{cat.name}</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">{cat.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              You can explore all these categories and more through our organized navigation on the
              <a href="/" className="text-primary-600 hover:text-primary-700 underline"> Sugargoo Sheet homepage</a>. Each category links directly to curated
              product collections on our main website, making it effortless to start shopping.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              Sugargoo Spreadsheet vs Traditional Searching
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              To truly appreciate the value of a <strong>Sugargoo Spreadsheet</strong>, it helps to understand the
              stark difference between using one and the traditional method of searching product listings manually.
              Here is a direct comparison based on real shopping scenarios:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b-2 border-background-200">
                    <th className="text-left py-3 px-3 font-medium text-foreground-800 whitespace-nowrap">Aspect</th>
                    <th className="text-left py-3 px-3 font-medium text-primary-700 whitespace-nowrap">With Sugargoo Spreadsheet</th>
                    <th className="text-left py-3 px-3 font-medium text-foreground-500 whitespace-nowrap">Traditional Searching</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { aspect: 'Research Time', good: '20-30 minutes per haul', bad: '4-6 hours per haul' },
                    { aspect: 'Product Quality', good: 'Pre-vetted by community', bad: 'Uncertain until delivery' },
                    { aspect: 'Price Awareness', good: 'Clear price ranges visible', bad: 'Hard to gauge fair value' },
                    { aspect: 'Discovery', good: 'Hidden gems surfaced', bad: 'Only popular listings found' },
                    { aspect: 'Dead Links', good: 'Regularly cleaned up', bad: 'Frequent frustration' },
                    { aspect: 'Learning Curve', good: 'Beginner friendly', bad: 'Steep, confusing' },
                  ].map((row, i) => (
                    <tr key={row.aspect} className={`border-b border-background-100 ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/40'}`}>
                      <td className="py-3 px-3 text-foreground-700 font-medium whitespace-nowrap">{row.aspect}</td>
                      <td className="py-3 px-3 text-primary-700 whitespace-nowrap">{row.good}</td>
                      <td className="py-3 px-3 text-foreground-500 whitespace-nowrap">{row.bad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              Tips for Beginners Starting with Sugargoo
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              If you are new to the world of Chinese shopping agents and <strong>Sugargoo Spreadsheet</strong> resources,
              here are some practical tips to help you get started on the right foot:
            </p>
            <ul className="space-y-3">
              {[
                'Start small — build a haul of 3-5 items for your first order rather than going all-in on a massive shipment. This lets you learn the process and assess quality without risking too much money.',
                'Always check estimated shipping weight before finalizing your cart. International shipping from China is calculated by weight, and heavy items like shoes and jackets can add up quickly.',
                'Pay attention to the exchange rate when budgeting. Prices in spreadsheets are often shown in CNY (Chinese Yuan), so use a currency converter to understand the actual cost in your local currency.',
                'Join community forums and discussion groups where Sugargoo Spreadsheet users share their experiences, recent finds, and honest reviews. The collective knowledge of the community is incredibly valuable.',
                'Do not ignore the QC photos. When your items arrive at the Sugargoo warehouse, carefully review the inspection photos before approving shipment. If something looks off, you can request a return or exchange.',
                'Bookmark our <a href="/" class="text-primary-600 hover:text-primary-700 underline">Sugargoo Sheet homepage</a> for quick access to all product categories whenever you are ready to shop.',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0 rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-lightbulb-line text-xs"></i>
                  </span>
                  <span className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: tip }}></span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              Common Mistakes to Avoid
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Even experienced shoppers sometimes fall into these common traps when using a <strong>Sugargoo Spreadsheet</strong>.
              Being aware of them upfront can save you time, money, and frustration:
            </p>
            <div className="space-y-3">
              {[
                { mistake: 'Ignoring Seller Ratings', fix: 'A tempting price means nothing if the seller has a history of sending wrong items or poor quality products. Always check seller feedback before adding to cart.' },
                { mistake: 'Skipping Size Charts', fix: 'Asian sizing runs significantly smaller than Western sizing in most cases. Never assume your usual size — always compare your measurements against the provided size chart for each individual item.' },
                { mistake: 'Not Checking Dead Links', fix: 'If a spreadsheet has not been updated recently, many links may be broken. Test a few random links before investing time browsing, or stick with actively maintained spreadsheets like the ones we organize.' },
                { mistake: 'Buying Everything at Once', fix: 'It is exciting to find dozens of great products, but remember that shipping costs scale with weight. A 10kg haul costs significantly more to ship than a 3kg one, and oversized items may incur additional fees.' },
                { mistake: 'Neglecting Customs Regulations', fix: 'Different countries have different import limits and restricted items. Research what can and cannot be shipped to your country before building your haul to avoid having items seized at customs.' },
              ].map((item, i) => (
                <div key={i} className="p-4 bg-background-100/60 border-l-3 border-accent-400 rounded-r-lg">
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0 text-accent-600">
                      <i className="ri-error-warning-line text-sm"></i>
                    </span>
                    <div>
                      <strong className="text-sm text-foreground-800 block mb-0.5">{item.mistake}</strong>
                      <span className="text-xs md:text-sm text-foreground-600">{item.fix}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
              Frequently Asked Questions About Sugargoo Spreadsheet
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: 'What is a Sugargoo Spreadsheet?',
                  a: 'A Sugargoo Spreadsheet is a curated database of products available through the Sugargoo shopping agent, organized by category with direct links, price information, QC photos, size charts, and community reviews. It serves as a one-stop research tool for finding verified products from Chinese marketplaces like Taobao, Weidian and 1688.',
                },
                {
                  q: 'Are Sugargoo Spreadsheets free to use?',
                  a: 'Yes, the vast majority of Sugargoo Spreadsheet resources are freely shared by community members and independent curators. Our platform organizes and links to these resources at no cost. You only pay for the products you choose to purchase and the associated shipping fees through Sugargoo.',
                },
                {
                  q: 'How often are Sugargoo Spreadsheets updated?',
                  a: 'Update frequency varies by curator. The best Sugargoo Spreadsheet resources are updated weekly or bi-weekly to remove dead links, add new discoveries, and refresh pricing information. Our platform prioritizes linking to actively maintained spreadsheets to ensure you always have access to current product data.',
                },
                {
                  q: 'Can I trust the product quality listed in a Sugargoo Spreadsheet?',
                  a: 'Products in well-maintained spreadsheets typically go through community vetting. However, quality can vary between sellers and batches. Always review QC photos provided by Sugargoo when your items arrive at their warehouse, and check community feedback for specific items before purchasing.',
                },
                {
                  q: 'How do I start using a Sugargoo Spreadsheet?',
                  a: 'Start by visiting our <a href="/" class="text-primary-600 hover:text-primary-700 underline">homepage</a> and browsing the category navigation. Pick a category that interests you, explore the curated product collections linked to our main website, and copy the product links into your Sugargoo account to begin building your haul.',
                },
                {
                  q: 'What is the difference between Sugargoo and other shopping agents?',
                  a: 'Sugargoo is known for competitive shipping rates, reliable QC photo service, and a user-friendly platform. While there are other agents like Pandabuy and CSSBuy, Sugargoo has built a strong reputation for transparency in pricing and consistent service quality. The agent you choose ultimately comes down to personal preference and shipping destination.',
                },
              ].map((faq, i) => (
                <div key={i} className="border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFaq(i)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100/40 transition-colors duration-200"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {activeFaq === i ? (
                        <i className="ri-subtract-line text-foreground-400"></i>
                      ) : (
                        <i className="ri-add-line text-foreground-400"></i>
                      )}
                    </span>
                  </button>
                  {activeFaq === i && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.a }}></p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Final Thoughts */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
              Final Thoughts and Next Steps
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              A <strong>Sugargoo Spreadsheet</strong> is hands-down one of the most effective tools available for anyone
              who wants to shop Chinese marketplaces intelligently. It transforms what could be an overwhelming and
              time-consuming process into a streamlined, enjoyable experience where you can confidently discover
              high-quality products at excellent prices.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              The key is finding and using a well-maintained spreadsheet that matches your shopping style and product
              interests. Whether you are into sneakers, streetwear, accessories, or home goods, there is likely a
              <strong>Sugargoo Spreadsheet</strong> out there that has already done the hard work of curating
              the best products in that category.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Our mission at <strong>Sugargoo Sheet</strong> is to make these resources as accessible and easy to use
              as possible. <a href="/" className="text-primary-600 hover:text-primary-700 underline">Return to our homepage</a> to start exploring
              organized product categories, or head directly to our main website to begin building your next haul.
            </p>
            <div className="bg-primary-50/50 border border-primary-200/50 rounded-lg p-5 md:p-6 mt-6">
              <h3 className="font-heading text-lg text-primary-800 mb-3">Ready to Start Shopping?</h3>
              <p className="text-sm text-primary-700 mb-4 leading-relaxed">
                Browse our organized product categories and discover thousands of verified finds. Each category links
                directly to curated collections on our main website — no more endless manual searching required.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/"
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Back to Homepage
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <button
                  onClick={handleGoToMain}
                  className="inline-flex items-center justify-center gap-2 border border-primary-300 text-primary-700 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Visit Main Website
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </article>

      <FooterSection />
    </div>
  );
}